import { Badge } from "../ui/Badge";
import type { OutputEvidenceItem, OutputGroundingSummary } from "../../../../shared/types";

interface EvidenceDrawerProps {
  grounding: OutputGroundingSummary | null | undefined;
  evidence: OutputEvidenceItem[];
  isLoading?: boolean;
  errorMessage?: string | null;
}

function sanitizeEvidenceUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return parsed.toString();
    }
  } catch {
    return null;
  }
  return null;
}

function summaryCopy(grounding: OutputGroundingSummary | null | undefined, evidenceCount: number): {
  badge: string;
  variant: "default" | "success" | "warning";
  body: string;
} {
  switch (grounding?.state) {
    case "grounded":
      return {
        badge: "Grounded",
        variant: "success",
        // Review classifies a draft grounded by its linked source finding even
        // when no evidence extracts were stored for this revision.
        body: evidenceCount > 0
          ? `Grounded in ${evidenceCount} source${evidenceCount === 1 ? "" : "s"}.`
          : "Has supporting evidence.",
      };
    case "partial":
      return {
        badge: "Partial sources",
        variant: "warning",
        body: "Some supporting evidence is incomplete or missing source metadata.",
      };
    case "source_expired":
      return {
        badge: "Source expired",
        variant: "warning",
        body: "Original payload expired. Retained extracts and citations remain available.",
      };
    case "ungrounded":
      return {
        badge: "Ungrounded",
        variant: "warning",
        body: "Generated without stored supporting evidence.",
      };
    case "not_instrumented":
    default:
      return {
        badge: "Not instrumented",
        variant: "default",
        body: "Grounding is not instrumented for this output yet.",
      };
  }
}

export function EvidenceDrawer({
  grounding,
  evidence,
  isLoading = false,
  errorMessage = null,
}: EvidenceDrawerProps) {
  const evidenceCount = grounding?.evidence_count ?? evidence.length;
  const copy = summaryCopy(grounding, evidenceCount);

  return (
    <details className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Data behind this</h2>
          <p className="mt-1 text-sm text-slate-600">{copy.body}</p>
        </div>
        <div className="flex items-center gap-2">
          {evidenceCount > 0 ? (
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
              {evidenceCount} source{evidenceCount === 1 ? "" : "s"}
            </span>
          ) : null}
          <Badge variant={copy.variant}>{copy.badge}</Badge>
        </div>
      </summary>

      <div className="mt-4 space-y-3">
        {isLoading ? (
          <p className="text-sm text-slate-600">Loading supporting evidence…</p>
        ) : errorMessage ? (
          <p className="text-sm text-amber-700">{errorMessage}</p>
        ) : evidence.length > 0 ? (
          evidence.map((item) => {
            const safeUrl = sanitizeEvidenceUrl(item.url);
            return (
              <div key={`${item.raw_datum_id}:${item.relation}`} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="info">{item.relation}</Badge>
                  {item.partial ? <Badge variant="warning">Partial</Badge> : null}
                  {item.label ? <p className="text-sm font-medium text-slate-900">{item.label}</p> : null}
                </div>
                {safeUrl ? (
                  <a
                    href={safeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex text-xs text-blue-600 hover:underline"
                  >
                    Open source
                  </a>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">No external source URL retained for this evidence item.</p>
                )}
                {item.extract ? (
                  <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700">{item.extract}</p>
                ) : (
                  <p className="mt-3 text-sm text-slate-500">No retained extract available.</p>
                )}
              </div>
            );
          })
        ) : (
          <p className="text-sm text-slate-600">{copy.body}</p>
        )}
      </div>
    </details>
  );
}
